import {
  fuzzyMatch,
  formatBoolean,
  formatFloat,
  formatInt,
  formatPrice,
} from "@/lib/printess/utils.js";
import {
  NAME_KEY,
  KIND_KEY,
  PRICE_KEY,
  SHOW_KEY,
  DIETARY_PREFERENCES_KEY,
} from "@/lib/printess/constants";
import { KINDS } from "./constants";

export default class Row {
  #row;
  #isLocked;

  constructor(row, isLocked = false) {
    this.#row = row;

    // Row may not be changed after it is locked.
    // Rows are unlocked if they are part of the default template.
    // Once they are modified, they are locked.
    // Any added rows are also locked.
    this.#isLocked = isLocked;
  }

  get isUnlocked() {
    return !this.#isLocked;
  }

  // Products are either of type KINDS.PRODUCT or without a type at all.
  // (we default all items to product)
  get isProduct() {
    return !this.kind || this.kind === KINDS.PRODUCT;
  }

  get isCategory() {
    return this.kind === KINDS.CATEGORY;
  }

  matches(name) {
    return fuzzyMatch(name, this.name);
  }

  copyAsTemplate() {
    return new this.constructor({ ...this.#row, name: null, show: true });
  }

  toJSON() {
    return this.#row;
  }

  setCategory() {
    this.kind = KINDS.CATEGORY;
  }

  updateFromMenuItem(menuItem, { lock = true, preserve = [] } = {}) {
    // these should never be mapped from the menu
    const preservedAttributes = [KIND_KEY, SHOW_KEY, ...preserve];

    for (const attr in menuItem) {
      if (preservedAttributes.includes(attr)) continue;

      this.#updateAttrFromMenuItem(attr, menuItem);
    }

    // Don't allow updates to this after it has been updated.
    this.#isLocked = lock;
  }

  differsFromMenuItem(menuItem, { ignore = [] } = {}) {
    const ignoredAttributes = [KIND_KEY, SHOW_KEY, ...ignore];

    for (const attr in menuItem) {
      if (ignoredAttributes.includes(attr)) continue;
      if (this.#formatAttrFromMenuItem(attr, menuItem) !== this.#row[attr]) {
        console.debug(
          "Row differs from menu item",
          attr,
          menuItem[attr],
          this.#row[attr],
        );
        return true;
      }
    }

    return false;
  }

  get name() {
    return this.#row[NAME_KEY];
  }

  set name(value) {
    this.#row[NAME_KEY] = value;
  }

  get kind() {
    return this.#row[KIND_KEY];
  }

  set kind(value) {
    this.#row[KIND_KEY] = value;
  }

  #formatAttrFromMenuItem(attr, menuItem) {
    const value = menuItem[attr];

    switch (attr) {
      case DIETARY_PREFERENCES_KEY:
        // TODO
        break;
      case PRICE_KEY:
        return formatPrice(value);
      default:
        switch (typeof value) {
          case "boolean":
            return formatBoolean(value);
          case "number":
            if (Number.isInteger(value)) {
              return formatInt(value);
            }
            return formatFloat(value);
          default:
            return value;
        }
    }
  }

  #updateAttrFromMenuItem(attr, menuItem) {
    if (attr == DIETARY_PREFERENCES_KEY) {
      return this.#updateDietaryPreferences(menuItem[attr]);
    }

    return this.#updateAttr(attr, this.#formatAttrFromMenuItem(attr, menuItem));
  }

  #updateDietaryPreferences(dietaryPreferences) {
    // They are all booleans
    for (const attr in dietaryPreferences) {
      this.#updateAttr(attr, formatBoolean(dietaryPreferences[attr]));
    }
  }

  #updateAttr(attr, value) {
    if (value === undefined) {
      return this.#updateAttr(attr, null);
    }

    this.#row[attr] = value;
  }
}
